import React, { useMemo, useState } from 'react';
import { Button, message, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import AccountFormDrawer from './components/AccountFormDrawer';
import FilterArea, { DEFAULT_FILTER_OPTIONS, STATUS_OPTIONS } from './components/FilterArea';
import { GET_ACCOUNT_LISTING_WITH_FILTERS, GetAccountListingWithFiltersResponse, AccountFiltersInput } from './graphql/queries/getAccountListingWithFilters';
import { useLazyQuery } from '@apollo/client';
import { AccountType } from 'graphql/types/AccountType';
import { getAccountDspManagerByAccountId } from './graphql/queries/getAccountDspManagerByAccountId';
import { getAccountWarehouseOperatorByAccountId } from './graphql/queries/getAccountWarehouseOperatorByAccountId';
import { getAccountWarehouseManagerByAccountId } from './graphql/queries/getAccountWarehouseManagerByAccountId';
import { getAccountPickupDispatcherByAccountId } from './graphql/queries/getAccountPickupDispatcherByAccountId';
import { getAccountPickupStaffByAccountId } from './graphql/queries/getAccountPickupStaffByAccountId';
import { getAccountPickupDriverByAccountId } from '../../../graphql/queries/getAccountPickupDriverByAccountId';
import { AccountFormData, useAccountApis } from '../../../hooks/useAccountApis';
import { FilterOptions } from './components/FilterArea';
import { showError } from 'utils/exceptionUtils';
import { useTranslation } from 'react-i18next';
import {getAccountTmsDriverByAccountId} from "../../../graphql/queries/getAccountTmsDriverByAccountId";
import {getAccountTmsDispatcherByAccountId} from "./graphql/queries/getAccountTmsDispatcherByAccountId";
import TablePaginationBar from '../../../components/TablePaginationBar';

const AccountPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { createAccount, updateAccount } = useAccountApis();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<AccountFormData | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [tempFilterOptions, setTempFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [accounts, setAccounts] = useState<AccountType[]>([]);

    const [getAccountListingWithFilters, { loading, error }] = useLazyQuery<GetAccountListingWithFiltersResponse, { filters: AccountFiltersInput }>(GET_ACCOUNT_LISTING_WITH_FILTERS);

    const getStatusLabel = (status: number) => {
        const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
        if (!statusOption) return 'Unknown';
        
        switch(i18n.language) {
            case 'zh':
            case 'zh-CN':
                return statusOption.labelZh;
            case 'es':
                return statusOption.labelEs;
            case 'en':
            default:
                return statusOption.labelEn;
        }
    };

    // Show new account drawer
    const showNewAccountDrawer = () => {
        setSelectedAccount(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show account details
    const showAccountDetail = async (account: AccountType) => {
        console.log('showAccountDetail', account);
        // Convert AccountData to AccountFormData
        const formData: AccountFormData = {
            id: account.id,
            phone: {
                countryCode: account.countryCode,
                phoneNumber: account.phone,
            },
            name: account.name,
            email: account.email,
            role: account.role.name,
            status: account.status,
            isOnDuty: true
        };

        const role = account.role.name;
        if (role === 'DSP_MANAGER' || role === 'DSP_OWNER_TEAM') {
            const dspManager = await getAccountDspManagerByAccountId(account.id);
            formData.dspId = dspManager?.dspId || 0;
        } else if (role === 'WAREHOUSE_MANAGER' || role === 'HUB_MANAGER') {
            const warehouseManager = await getAccountWarehouseManagerByAccountId(account.id);
            formData.warehouseId = warehouseManager.warehouseId;
        } else if (role === 'WAREHOUSE_OPERATOR' || role === 'HUB_OPERATOR' || role === 'SORTING_STAFF' || role === 'SORTER_OPERATOR' || role === 'WAREHOUSE_TAG_SORTER') {
            const warehouseOperator = await getAccountWarehouseOperatorByAccountId(account.id);
            formData.warehouseId = warehouseOperator.warehouseId;
        } else if (role === 'PICKUP_STAFF') {
            const pickupStaff = await getAccountPickupStaffByAccountId(account.id);
            formData.warehouseId = pickupStaff.warehouseId;
        } else if (role === 'PICKUP_DISPATCHER') {
            const pickupDispatcher = await getAccountPickupDispatcherByAccountId(account.id);
            formData.warehouseId = pickupDispatcher.warehouseId;
        } else if (role === 'PICKUP_DRIVER') {
            const pickupDriver = await getAccountPickupDriverByAccountId(account.id);
            formData.warehouseId = pickupDriver.warehouseId;
            formData.vehicleId = pickupDriver.vehicleId;
            formData.workWeekdays = JSON.parse(pickupDriver.workWeekdaysJson || '[1,2,3,4,5]');
            formData.workStartTime = pickupDriver.workStartTime;
            formData.workEndTime = pickupDriver.workEndTime;
            formData.responsibleZipcodes = pickupDriver.responsibleZipcodeListJson ? 
                JSON.parse(pickupDriver.responsibleZipcodeListJson) : [];
        }else if (role === 'TMS_DISPATCHER') {
            const tmsDispatcher = await getAccountTmsDispatcherByAccountId(account.id);
            formData.warehouseId = tmsDispatcher.warehouseId;
        } else if (role === 'TMS_DRIVER') {
            const tmsDriver = await getAccountTmsDriverByAccountId(account.id);
            formData.vehicleId = tmsDriver.vehicleId;
            formData.carrierId = tmsDriver.carrierId ;
        }
        setSelectedAccount(formData);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle account form submission (create or update)
    const handleAccountSubmit = async (values: AccountFormData) => {
        if (drawerMode === 'create') {
            try {
                console.log('New account submitted:', values);
                const result = await createAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('central.personnel.messages.personnelCreatedSuccessfully'));
                // 重新搜索以刷新数据
                await performSearch(filterOptions);
            } catch (error) {
                console.error('Error creating account:', error);
                showError(error);
            }
        } else {
            try {
                console.log('Personnel updated:', values);
                const result = await updateAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('central.personnel.messages.personnelUpdatedSuccessfully')); 
                // 重新搜索以刷新数据
                await performSearch(filterOptions);
            } catch (error) {
                console.error('Error updating account:', error);
                showError(error);
            }
        }
    };

    // Table column definitions
    const columns: TableColumnsType<AccountType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            width: 100,
            ellipsis: true,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showAccountDetail(record)}
                    style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('central.personnel.columns.name'),
            dataIndex: 'name',
            key: 'name',
            align: 'left' as const,
            width: 150,
            ellipsis: true,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.personnel.columns.phone'),
            dataIndex: 'phone',
            key: 'phone',
            align: 'left' as const,
            width: 150,
            ellipsis: true,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.personnel.columns.role'),
            dataIndex: ['role', 'name'],
            key: 'role.name',
            align: 'left' as const,
            width: 180,
            ellipsis: true,
            render: (_, record) => {
                const text = t(`enum.role.${record.role.name}`);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('central.personnel.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            width: 120,
            ellipsis: true,
            render: (status: number) => {
                const color = status === 1 ? 'text-green-600' : 'text-gray-400';
                const text = getStatusLabel(status);
                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
    ];

    const handleApplyFilters = async () => {
        setFilterOptions(tempFilterOptions);
        setPagination(prev => ({ ...prev, current: 1 }));
        await performSearch(tempFilterOptions);
    };

    const handleResetFilters = async () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setPagination(prev => ({ ...prev, current: 1 }));
        await performSearch(DEFAULT_FILTER_OPTIONS);
    };

    // 执行搜索
    const performSearch = async (filters: FilterOptions) => {
        const result = await getAccountListingWithFilters({
            variables: {
                filters: {
                    role: filters.role === 'all' ? '' : filters.role,
                    id: filters.id,
                    name: filters.name,
                    phone: filters.phone,
                    status: filters.status === 2 ? undefined : filters.status // 0 表示不过滤状态
                }
            }
        });

        if (result.data) {
            setAccounts(result.data.getAccountListingWithFilters);
        }
    };

    // Handle pagination change
    const handleTableChange = (newPagination: any) => {
        setPagination(newPagination);
    };

    const pagedAccounts = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        const end = start + pagination.pageSize;
        return accounts.slice(start, end);
    }, [accounts, pagination.current, pagination.pageSize]);

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter area */}
                <FilterArea filters={tempFilterOptions} setFilters={setTempFilterOptions} />

                {/* 操作按钮：同一行右侧 */}
                <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                    <Button
                        type="primary"
                        onClick={showNewAccountDrawer}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.personnel.filters.new')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleApplyFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                </div>

                {/* Personnel table */}
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={pagedAccounts}
                    pagination={false}
                    scroll={{x: 'max-content'}}
                    size="middle"
                    tableLayout="fixed"
                    className="mb-4"
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={accounts.length}
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
                    onShowSizeChange={(_, size) => handleTableChange({ current: 1, pageSize: size })}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* Personnel form drawer (shared for create/edit) */}
                <AccountFormDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onSubmit={handleAccountSubmit}
                    account={selectedAccount}
                    mode={drawerMode}
                />

            </div>
    );
};

export default AccountPage;